import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { Persona, StudioId } from '@/domain/types';

export interface SessionState {
  persona: Persona | null;
  agentId: string | null;
  artistId: string | null;
  apprenticeId: string | null;
  assignedStudios: StudioId[];
  activeStudio: StudioId | null;
  activeCustomerId: string | null;
  loggedInAt: string | null;
  // Comms read-state: maps channelId or dmThreadId → last-read ISO timestamp.
  // Lives in session (not the brain) so per-persona reads don't leak into the
  // shared fixture set.
  lastReadByChannel: Record<string, string>;
  setPersona: (
    persona: Persona,
    opts?: {
      agentId?: string;
      artistId?: string;
      apprenticeId?: string;
      assignedStudios?: StudioId[];
      activeStudio?: StudioId;
    },
  ) => void;
  setActiveStudio: (studio: StudioId) => void;
  setActiveCustomer: (id: string | null) => void;
  markCommsRead: (id: string, iso: string) => void;
  logout: () => void;
}

const initialState = {
  persona: null,
  agentId: null,
  artistId: null,
  apprenticeId: null,
  assignedStudios: [] as StudioId[],
  activeStudio: null,
  activeCustomerId: null,
  loggedInAt: null,
  lastReadByChannel: {} as Record<string, string>,
};

export const useSessionStore = create<SessionState>()(
  persist(
    (set) => ({
      ...initialState,
      setPersona: (persona, opts) =>
        set({
          persona,
          agentId: opts?.agentId ?? null,
          artistId: opts?.artistId ?? null,
          apprenticeId: opts?.apprenticeId ?? null,
          assignedStudios: opts?.assignedStudios ?? [],
          activeStudio: opts?.activeStudio ?? opts?.assignedStudios?.[0] ?? null,
          loggedInAt: new Date().toISOString(),
        }),
      setActiveStudio: (studio) => set({ activeStudio: studio }),
      setActiveCustomer: (id) => set({ activeCustomerId: id }),
      markCommsRead: (id, iso) =>
        set((s) => ({ lastReadByChannel: { ...s.lastReadByChannel, [id]: iso } })),
      logout: () => set(initialState),
    }),
    {
      name: 'ati-session',
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);
